'use client'

import type { ClassAttributes, HTMLAttributes } from 'react'
import ReactMarkdown from 'react-markdown'
import type { ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { containsMarkdown } from '../utils'
import { CodeBlock } from './CodeBlock'

interface TextBlockProps {
  content: string
  isActionResult?: boolean
}

/**
 * Renders text content. If the content contains markdown formatting
 * it is rendered through ReactMarkdown with @tailwindcss/typography
 * prose classes. Otherwise it renders as plain text with whitespace
 * preservation.
 *
 * Code fences are rendered via the dedicated CodeBlock component
 * which provides copy-to-clipboard.
 */
export function TextBlock({ content, isActionResult }: TextBlockProps) {
  const isMarkdown = containsMarkdown(content)

  if (!isMarkdown) {
    return <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
  }

  return (
    <article className="prose prose-sm prose-slate dark:prose-invert max-w-none select-text prose-p:my-2 prose-p:leading-relaxed prose-headings:font-semibold prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-pre:p-0 prose-pre:bg-transparent prose-code:before:content-none prose-code:after:content-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code(props: ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement> & ExtraProps) {
            const { className, children, ...rest } = props
            const inline = !(className && /language-/.test(className))
            const language = className?.replace('language-', '') || ''
            const codeString = String(children).replace(/\n$/, '')

            // Fenced code blocks get the CodeBlock component
            if (!inline && (language || codeString.includes('\n'))) {
              // For action results, try to pretty-print JSON
              if (isActionResult) {
                try {
                  const parsed = JSON.parse(codeString)
                  return (
                    <CodeBlock
                      code={JSON.stringify(parsed, null, 2)}
                      language={language || 'json'}
                    />
                  )
                } catch {
                  // Not JSON — fall through to standard rendering
                }
              }

              return <CodeBlock code={codeString} language={language} />
            }

            // Inline code
            return (
              <code
                className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono"
                {...rest}
              >
                {children}
              </code>
            )
          },
          table({ children }) {
            return (
              <div className="not-prose my-4 overflow-x-auto rounded-lg border border-border">
                <table className="min-w-full divide-y divide-border text-sm">
                  {children}
                </table>
              </div>
            )
          },
          thead({ children }) {
            return (
              <thead className="bg-muted/50">{children}</thead>
            )
          },
          tbody({ children }) {
            return (
              <tbody className="divide-y divide-border">{children}</tbody>
            )
          },
          th({ children }) {
            return (
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {children}
              </th>
            )
          },
          td({ children }) {
            return (
              <td className="px-4 py-2.5 text-sm">{children}</td>
            )
          },
          tr({ children }) {
            return (
              <tr className="transition-colors hover:bg-muted/30">{children}</tr>
            )
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  )
}
